import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { supabase } from '@/lib/supabase';
import { toSimplified } from '@/i18n/utils/zhConverter';

interface Category {
  id: string;
  name: string;
  slug: string;
}

interface ArticleFormData {
  title: string;
  content: string;
  summary: string;
  image_url: string;
  category_id: string;
  tags: string;
  is_published: boolean;
  article_type: 'original' | 'embedded';
  source_url: string;
  source_name: string;
  source_thanks_text: string;
}

const EMPTY_FORM: ArticleFormData = {
  title: '',
  content: '',
  summary: '',
  image_url: '',
  category_id: '',
  tags: '',
  is_published: false,
  article_type: 'original',
  source_url: '',
  source_name: '',
  source_thanks_text: '',
};

export default function AdminEdit() {
  const { isAuthenticated, isChecking } = useAdminAuth();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const isNew = slug === 'new';

  const [form, setForm] = useState<ArticleFormData>(EMPTY_FORM);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [existingSlug, setExistingSlug] = useState<string>('');
  const [fetchingContent, setFetchingContent] = useState(false);
  const [fetchMessage, setFetchMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const saveModeRef = useRef<'draft' | 'publish' | null>(null);
  // AI Image Generation
  const [showAiGen, setShowAiGen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  // Image preview state
  const [imageLoadError, setImageLoadError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    if (!isChecking && !isAuthenticated) {
      navigate('/admin', { replace: true });
      return;
    }
    if (!isAuthenticated) return;

    async function init() {
      try {
        const { data: catData, error: catError } = await supabase
          .from('categories')
          .select('id, name, slug')
          .order('sort_order', { ascending: true });

        if (catError) throw catError;
        setCategories(catData || []);

        if (!isNew && slug) {
          const { data: articleData, error: articleError } = await supabase
            .from('articles')
            .select('title, title_zh_tw, content, content_zh_tw, summary, summary_zh_tw, image_url, category_id, tags, is_published, article_type, source_url, source_name, source_thanks_text')
            .eq('slug', slug)
            .maybeSingle();

          if (articleError) throw articleError;

          if (articleData) {
            setExistingSlug(slug);
            // Load Traditional version as primary editing content
            setForm({
              title: articleData.title_zh_tw || articleData.title || '',
              content: articleData.content_zh_tw || articleData.content || '',
              summary: articleData.summary_zh_tw || articleData.summary || '',
              image_url: articleData.image_url || '',
              category_id: articleData.category_id || '',
              tags: (articleData.tags || []).join(', '),
              is_published: articleData.is_published || false,
              article_type: articleData.article_type || 'original',
              source_url: articleData.source_url || '',
              source_name: articleData.source_name || '',
              source_thanks_text: articleData.source_thanks_text || '',
            });
          } else {
            setError('文章未找到');
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载失败');
      } finally {
        setLoading(false);
      }
    }

    init();
  }, [isAuthenticated, isChecking, isNew, slug, navigate]);

  // Reset image loading state when URL changes
  useEffect(() => {
    if (form.image_url) {
      setImageLoadError(false);
      setImageLoading(true);
    } else {
      setImageLoadError(false);
      setImageLoading(false);
    }
  }, [form.image_url]);

  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\u4e00-\u9fff]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 80) || Date.now().toString(36);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setSaveError(null);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
      const filePath = `articles/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('article-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('article-images')
        .getPublicUrl(filePath);

      setForm((prev) => ({ ...prev, image_url: urlData.publicUrl }));
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : '图片上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (field: keyof ArticleFormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAiGenImage = () => {
    if (!aiPrompt.trim()) return;
    setAiGenerating(true);
    setImageLoadError(false);
    setImageLoading(true);

    const seqValue = 'article-img-' + Date.now() + '-' + Math.random().toString(36).substring(2, 8);
    const imgUrl = new URL('https://readdy.ai/api/search-image');
    imgUrl.searchParams.set('width', '800');
    imgUrl.searchParams.set('height', '500');
    imgUrl.searchParams.set('seq', seqValue);
    imgUrl.searchParams.set('orientation', 'landscape');
    imgUrl.searchParams.set('query', aiPrompt.trim());

    setForm((prev) => ({ ...prev, image_url: imgUrl.toString() }));

    setTimeout(() => {
      setAiGenerating(false);
      setShowAiGen(false);
      setAiPrompt('');
      setSaveSuccess('图片已生成，等待服务器就绪...');
      setTimeout(() => setSaveSuccess(null), 3000);
    }, 500);
  };

  const handleFetchContent = async () => {
    if (!form.source_url.trim()) {
      setFetchMessage('请先输入来源网址');
      return;
    }

    setFetchingContent(true);
    setFetchMessage(null);
    setSaveError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('fetch-article-content', {
        body: { url: form.source_url.trim() },
      });

      if (fnError) throw new Error(fnError.message);
      if (!data?.success) throw new Error(data?.error || '抓取失败');

      const fetched = data.data;
      const pageTitle = fetched.pageTitle || '';

      const sourceName = form.source_name.trim() || '原文作者';
      const thanksText = form.source_thanks_text.trim()
        || `特别感谢 ${sourceName} 提供本文的宝贵内容，本页面仅为引用展示，版权归原作者所有。`;

      const attributionHtml = `
<hr style="margin-top:2rem;border:1px solid #e5e7eb;" />
<div style="margin-top:1rem;padding:1rem;background:#f9fafb;border-radius:0.5rem;border-left:3px solid #6b7280;">
  <p style="margin:0;font-size:0.875rem;color:#6b7280;"><strong>📎 文章来源：</strong>${sourceName}</p>
  <p style="margin:0.25rem 0 0;font-size:0.875rem;color:#6b7280;">${thanksText}</p>
  <p style="margin:0.5rem 0 0;font-size:0.8125rem;">
    <a href="${form.source_url.trim()}" target="_blank" rel="nofollow noopener" style="color:#3b82f6;">🔗 查看原文</a>
  </p>
</div>`;

      setForm((prev) => ({
        ...prev,
        title: pageTitle || prev.title,
        content: fetched.content + attributionHtml,
        summary: prev.summary.trim() || fetched.summary || '',
      }));

      setFetchMessage('内容抓取成功！');
      setTimeout(() => setFetchMessage(null), 3000);
    } catch (err) {
      setFetchMessage('内容抓取失败，请检查网址是否正确或手动输入内容');
      setSaveError(err instanceof Error ? err.message : '抓取失败');
    } finally {
      setFetchingContent(false);
    }
  };

  const handleSaveDraft = () => {
    saveModeRef.current = 'draft';
    formRef.current?.requestSubmit();
  };

  const handleSavePublish = () => {
    saveModeRef.current = 'publish';
    formRef.current?.requestSubmit();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);

    try {
      if (!form.title.trim()) {
        throw new Error('文章标题不能为空');
      }
      if (form.article_type === 'original' && !form.content.trim()) {
        throw new Error('文章内容不能为空');
      }
      if (form.article_type === 'embedded' && !form.source_url.trim()) {
        throw new Error('来源网址不能为空');
      }

      const newSlug = isNew ? generateSlug(form.title) : existingSlug;
      const mode = saveModeRef.current;
      saveModeRef.current = null;

      const finalPublished = mode === 'draft'
        ? false
        : mode === 'publish'
          ? true
          : form.is_published;

      // Auto-generate Simplified Chinese from the Traditional input
      const titleSimp = toSimplified(form.title.trim());
      const contentSimp = toSimplified(form.content);
      const summarySimp = toSimplified(form.summary.trim() || form.content.replace(/<[^>]*>/g, '').substring(0, 150));

      const defaultThanks = `特别感谢 ${form.source_name.trim() || '原文作者'} 提供本文的宝贵内容，本页面仅为引用展示，版权归原作者所有。`;

      const articlePayload = {
        title: titleSimp,
        title_zh_tw: form.title.trim(),
        content: contentSimp,
        content_zh_tw: form.content,
        summary: summarySimp,
        summary_zh_tw: form.summary.trim() || form.content.replace(/<[^>]*>/g, '').substring(0, 150),
        image_url: form.image_url,
        category_id: form.category_id || null,
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        is_published: finalPublished,
        article_type: form.article_type,
        source_url: form.article_type === 'embedded' ? form.source_url.trim() : null,
        source_name: form.article_type === 'embedded' ? (form.source_name.trim() || null) : null,
        source_thanks_text: form.article_type === 'embedded' ? (form.source_thanks_text.trim() || defaultThanks) : null,
        updated_at: new Date().toISOString(),
      };

      if (isNew) {
        const { error: insertError } = await supabase
          .from('articles')
          .insert({ ...articlePayload, slug: newSlug });

        if (insertError) {
          if (insertError.code === '23505') {
            throw new Error(`URL 标识 "${newSlug}" 已存在，请修改文章标题后重试`);
          }
          throw insertError;
        }
      } else {
        const { error: updateError } = await supabase
          .from('articles')
          .update(articlePayload)
          .eq('slug', existingSlug);

        if (updateError) throw updateError;
      }

      setSaveSuccess(isNew ? '文章创建成功！' : '文章更新成功！');

      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (isChecking || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-100 px-4">
        <div className="text-center">
          <p className="text-foreground-500 text-sm mb-4">{error}</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="bg-background-200 hover:bg-background-300 text-foreground-700 px-5 py-2 rounded-full text-sm cursor-pointer"
          >
            返回管理后台
          </button>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-background-100">
      {/* Header */}
      <header className="bg-background-50 border-b border-background-200/70 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="w-8 h-8 rounded-lg bg-background-100 hover:bg-background-200 flex items-center justify-center text-foreground-500 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-sm" />
            </button>
            <h1 className="font-heading text-base font-bold text-foreground-900">
              {isNew ? '新建文章' : '编辑文章'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {!isNew && (
              <a
                href={`/article/${existingSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-foreground-500 hover:text-primary-600 px-3 py-1.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer inline-flex items-center gap-1"
              >
                <i className="ri-external-link-line text-xs" />
                预览
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Form */}
      <section className="max-w-4xl mx-auto px-4 md:px-8 py-6">
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
          {/* Status messages */}
          {saveError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2">
              <i className="ri-error-warning-line flex-shrink-0" />
              {saveError}
            </div>
          )}
          {saveSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 flex items-center gap-2">
              <i className="ri-check-line flex-shrink-0" />
              {saveSuccess}
            </div>
          )}

          {/* Language hint */}
          <div className="bg-accent-50 border border-accent-200 rounded-lg p-3 flex items-start gap-2">
            <i className="ri-information-line text-accent-600 text-sm mt-0.5 flex-shrink-0" />
            <p className="text-xs text-accent-800">
              输入<strong>繁体中文</strong>即可，系统会自动转换为简体中文。两个语言版本的前台会根据访客设置自动切换显示。
            </p>
          </div>

          {/* Article Type Toggle */}
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
            <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
              文章类型
            </label>
            <div className="flex gap-1 bg-background-100 rounded-lg p-1 w-fit">
              <button
                type="button"
                onClick={() => handleChange('article_type', 'original')}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  form.article_type === 'original'
                    ? 'bg-background-50 text-foreground-900 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className={`${form.article_type === 'original' ? 'ri-edit-box-line' : 'ri-edit-box-line'} mr-1.5 text-xs`} />
                原创文章
              </button>
              <button
                type="button"
                onClick={() => handleChange('article_type', 'embedded')}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  form.article_type === 'embedded'
                    ? 'bg-background-50 text-foreground-900 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-link mr-1.5 text-xs" />
                引用嵌入
              </button>
            </div>
            <p className="text-xs text-foreground-400 mt-2">
              {form.article_type === 'original'
                ? '手动编写原创内容'
                : '输入外部文章网址，自动抓取内容并注明来源'}
            </p>
          </div>

          {/* Embedded Article Fields */}
          {form.article_type === 'embedded' && (
            <>
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                  来源网址 *
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={form.source_url}
                    onChange={(e) => handleChange('source_url', e.target.value)}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="https://..."
                  />
                  <button
                    type="button"
                    onClick={handleFetchContent}
                    disabled={fetchingContent || !form.source_url.trim()}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  >
                    {fetchingContent ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        抓取中...
                      </>
                    ) : (
                      <>
                        <i className="ri-download-line text-sm" />
                        抓取内容
                      </>
                    )}
                  </button>
                </div>
                {fetchMessage && (
                  <p className={`text-xs mt-2 ${fetchMessage === '内容抓取成功！' ? 'text-green-600' : fetchMessage === '请先输入来源网址' ? 'text-amber-600' : 'text-red-600'}`}>
                    {fetchMessage}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                  <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                    来源名称
                  </label>
                  <input
                    type="text"
                    value={form.source_name}
                    onChange={(e) => handleChange('source_name', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="例如：维基百科、健康杂志等"
                  />
                </div>
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                  <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                    感谢语（留空使用默认文案）
                  </label>
                  <input
                    type="text"
                    value={form.source_thanks_text}
                    onChange={(e) => handleChange('source_thanks_text', e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder={form.source_name ? `特别感谢 ${form.source_name} 提供本文的宝贵内容...` : '输入来源名称后将自动生成默认感谢语'}
                  />
                </div>
              </div>
            </>
          )}

          {/* Title - Single field */}
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
            <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
              文章标题 *
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              placeholder="例如：維生素C：免疫力的守護者"
            />
            <p className="text-xs text-foreground-400 mt-1.5">
              输入繁体中文，保存时自动生成简体版本
            </p>
          </div>

          {/* Category & Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
              <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                文章分类
              </label>
              <select
                value={form.category_id}
                onChange={(e) => handleChange('category_id', e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer"
              >
                <option value="">-- 选择分类 --</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 flex flex-col justify-center">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className={`w-11 h-6 rounded-full transition-colors flex items-center px-0.5 ${form.is_published ? 'bg-green-500' : 'bg-background-300'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${form.is_published ? 'translate-x-5' : 'translate-x-0'}`} />
                </div>
                <div>
                  <span className="text-sm font-medium text-foreground-900">
                    {form.is_published ? '已发布' : '草稿'}
                  </span>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    {form.is_published ? '文章将对所有访客可见' : '仅管理员可见'}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={form.is_published}
                  onChange={(e) => handleChange('is_published', e.target.checked)}
                  className="sr-only"
                />
              </label>
            </div>
          </div>

          {/* Image Upload */}
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
            <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
              成分照片
            </label>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="w-full sm:w-64 h-40 rounded-lg bg-background-100 border-2 border-dashed border-background-300 overflow-hidden flex-shrink-0 relative">
                {form.image_url ? (
                  <>
                    {imageLoading && !imageLoadError && (
                      <div className="absolute inset-0 flex items-center justify-center bg-background-100 z-10">
                        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    {imageLoadError ? (
                      <div className="w-full h-full flex flex-col items-center justify-center text-red-400 gap-1">
                        <i className="ri-error-warning-line text-xl" />
                        <span className="text-xs text-center px-2">图片加载失败<br />请检查 URL 或稍后重试</span>
                      </div>
                    ) : (
                      <img
                        src={form.image_url}
                        alt="预览"
                        className="w-full h-full object-cover object-top"
                        onLoad={() => setImageLoading(false)}
                        onError={() => { setImageLoadError(true); setImageLoading(false); }}
                      />
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-foreground-400">
                    <i className="ri-image-add-line text-2xl mb-1" />
                    <span className="text-xs">暂无图片</span>
                  </div>
                )}
              </div>
              <div className="flex-1 flex flex-col justify-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 bg-secondary-100 hover:bg-secondary-200 text-secondary-800 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-secondary-500 border-t-transparent rounded-full animate-spin" />
                      上传中...
                    </>
                  ) : (
                    <>
                      <i className="ri-upload-cloud-line text-sm" />
                      选择图片上传
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAiGen(!showAiGen); setAiPrompt(''); }}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    showAiGen
                      ? 'bg-primary-500 text-white'
                      : 'bg-secondary-100 hover:bg-secondary-200 text-secondary-800'
                  }`}
                >
                  <i className="ri-magic-line text-sm" />
                  AI 生图
                </button>
                <p className="text-xs text-foreground-400">
                  支持 JPG、PNG、WebP 格式，建议尺寸 800×500 像素
                </p>
                {showAiGen && (
                  <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3 space-y-2">
                    <p className="text-xs text-foreground-500">
                      输入中文描述，AI 自动生成配图并填入预览区。描述越详细效果越好。
                    </p>
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => { setAiPrompt(e.target.value); }}
                      className="w-full px-3 py-2 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="一瓶维生素C补充剂放在纯白背景上，旁边点缀着新鲜的橙子和柠檬切片，柔和自然光从左侧打过来，产品摄影风格，干净简约"
                      rows={3}
                    />
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleAiGenImage}
                        disabled={aiGenerating || !aiPrompt.trim()}
                        className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {aiGenerating ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            生成中...
                          </>
                        ) : (
                          <>
                            <i className="ri-magic-line text-sm" />
                            生成图片
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowAiGen(false); setAiPrompt(''); }}
                        className="px-3 py-2 rounded-lg text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-xs text-foreground-500 mb-1 block">或输入图片 URL</label>
                  <input
                    type="text"
                    value={form.image_url}
                    onChange={(e) => { handleChange('image_url', e.target.value); setImageLoadError(false); }}
                    className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-200 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-all"
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Summary - Single field */}
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
            <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
              文章摘要
            </label>
            <textarea
              value={form.summary}
              onChange={(e) => handleChange('summary', e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
              placeholder="簡要描述文章內容，留空將自動從正文提取..."
              rows={3}
            />
            <p className="text-xs text-foreground-400 mt-1.5">
              留空将自动从正文前150字提取
            </p>
          </div>

          {/* Tags */}
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
            <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
              标签
            </label>
            <input
              type="text"
              value={form.tags}
              onChange={(e) => handleChange('tags', e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              placeholder="多个标签用逗号分隔，例如：维生素C, 免疫力, 抗氧化"
            />
          </div>

          {/* Content - Single field */}
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
            <label className="block text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
              文章内容 * <span className="text-foreground-400 font-normal normal-case">— 支持 HTML 格式</span>
            </label>
            <textarea
              value={form.content}
              onChange={(e) => handleChange('content', e.target.value)}
              className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none font-mono"
              placeholder="<h3>標題</h3><p>段落內容...</p>"
              rows={16}
            />
            <p className="text-xs text-foreground-400 mt-1.5">
              输入繁体中文，保存时自动生成简体版本
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-background-200/70">
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="bg-background-200 hover:bg-background-300 text-foreground-600 px-5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer inline-flex items-center gap-1.5"
            >
              <i className="ri-arrow-left-line text-sm" />
              取消
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={saving}
                className="px-5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50"
              >
                {saving && saveModeRef.current === 'draft' ? (
                  <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <i className="ri-save-line text-sm" />
                )}
                保存草稿
              </button>
              <button
                type="button"
                onClick={handleSavePublish}
                disabled={saving}
                className="px-5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer inline-flex items-center gap-1.5 bg-green-500 text-white hover:bg-green-600 disabled:opacity-50"
              >
                {saving && saveModeRef.current === 'publish' ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <i className="ri-send-plane-line text-sm" />
                )}
                保存并发布
              </button>
            </div>
          </div>
        </form>
      </section>
    </main>
  );
}